import { useMutation } from "@tanstack/react-query";
import api from "../../utils/api";
import { useInvalidateDomain } from "../../lib/invalidation";
import type {
  Quote,
  QuoteCalcInput,
  QuoteCommercialInput,
  QuoteEstimate,
  QuoteEstimateInputs,
  QuotePdfMeta,
  QuoteStatus,
} from "./types";

interface Envelope<T> {
  data?: { message?: string; data?: T };
}

export interface EstimateResult {
  estimate: QuoteEstimate;
  inputs: QuoteEstimateInputs;
}

/** Calcula sin guardar (no invalida nada). */
export function useEstimateQuote() {
  return useMutation({
    mutationFn: (input: QuoteCalcInput): Promise<EstimateResult> =>
      api.post<Envelope<EstimateResult>>("/quotes/estimate", input).then((res) => {
        const data = res.data?.data?.data;
        if (!data) throw new Error("estimate: respuesta vacía");
        return data;
      }),
  });
}

export type CreateQuoteInput = QuoteCalcInput & QuoteCommercialInput;

export function useCreateQuote() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (input: CreateQuoteInput): Promise<Quote & { inputs?: QuoteEstimateInputs }> =>
      api.post<Envelope<Quote>>("/quotes", input).then((res) => res.data?.data?.data as Quote),
    onSuccess: () => invalidateDomain("quotes"),
  });
}

export function useUpdateQuote() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateQuoteInput> }): Promise<Quote> =>
      api.put<Envelope<Quote>>("/quotes", data, { params: { id } }).then((res) => res.data?.data?.data as Quote),
    onSuccess: () => invalidateDomain("quotes"),
  });
}

export function useUpdateQuoteStatus() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: QuoteStatus }): Promise<Quote> =>
      api
        .put<Envelope<Quote>>("/quotes/status", { status }, { params: { id } })
        .then((res) => res.data?.data?.data as Quote),
    onSuccess: () => invalidateDomain("quotes"),
  });
}

export function useGenerateQuotePdf() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: ({ id, show_breakdown }: { id: string; show_breakdown?: boolean }): Promise<QuotePdfMeta> =>
      api
        .post<Envelope<QuotePdfMeta>>("/quotes/pdf", { show_breakdown }, { params: { id } })
        .then((res) => res.data?.data?.data as QuotePdfMeta),
    onSuccess: () => invalidateDomain("quotes"),
  });
}

export function useDeleteQuote() {
  const invalidateDomain = useInvalidateDomain();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.delete("/quotes", { params: { id } }).then(() => undefined),
    onSuccess: () => invalidateDomain("quotes"),
  });
}
